// ==========================================================================
// lib/supabase.ts — the one Supabase client (FF-002, 9 Oct 2026)
// Uses the PUBLISHABLE key only (safe in the browser; RLS decides what anyone
// can see). Never put a secret/service key in the web app.
// checkConnection() is the scaffold smoke test: it asks Supabase Auth's health
// endpoint whether the project is reachable with our key.
// ==========================================================================
import { createClient } from "@supabase/supabase-js";
import { previewFetch, readOnlyPreview } from "./previewSafety";

const url = import.meta.env.VITE_SUPABASE_URL;
const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

export const configMissing = !url || !key || url.includes("REPLACE") || key.includes("REPLACE");

export const supabase = createClient(url ?? "http://invalid.local", key ?? "missing", {
  global: readOnlyPreview ? { fetch: previewFetch } : undefined,
  auth: { persistSession: true, autoRefreshToken: true },
});

export type ConnectionResult = { ok: true } | { ok: false; reason: string };

export async function checkConnection(): Promise<ConnectionResult> {
  if (configMissing) return { ok: false, reason: "web/.env.local is missing or still has REPLACE values" };
  try {
    const res = await fetch(`${url}/auth/v1/health`, { headers: { apikey: key } });
    if (res.ok) return { ok: true };
    return { ok: false, reason: `Supabase answered ${res.status} — check the URL and key in web/.env.local` };
  } catch {
    return { ok: false, reason: "Could not reach Supabase — check the URL in web/.env.local and your internet" };
  }
}
